/**
 * 节流函数
 *
 * ```js
 * const throttleRet = FastUtils.throttle(function(e){
 *   console.log('throttle');
 * });
 *
 * window.addEventListener('scroll', throttleRet);
 *
 * // throttleRet 将不可再执行
 * throttleRet.cancel();
 * ```
 *
 * @category Function
 * @param callback 调用的函数
 * @param delay 延迟时间，默认200ms
 * @param callbackOptions 内部参数
 * @return 返回的值，可以调用 `cancel` 取消执行
 */
function throttle(callback, delay, callbackOptions) {
    if (delay === void 0) { delay = 200; }
    var isImmediate = true;
    var isDebounce = false;
    var timeoutID;
    var lastExec = 0;
    var ctx;
    var callbackArgs;
    var isCancelled = false;
    if (typeof callbackOptions === 'object') {
        isImmediate = 'isImmediate' in callbackOptions ? !!callbackOptions.isImmediate : isImmediate;
        isDebounce = 'isDebounce' in callbackOptions ? !!callbackOptions.isDebounce : isDebounce;
    }
    function clearExistingTimeout() {
        if (timeoutID !== undefined) {
            clearTimeout(timeoutID);
        }
    }
    function cancel() {
        clearExistingTimeout();
        lastExec = 0;
        isCancelled = true;
    }
    function exec() {
        lastExec = isImmediate ? Date.now() : 0;
        callback.apply(ctx, callbackArgs);
    }
    function wrapper() {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (isCancelled)
            return;
        var now = Date.now();
        if (lastExec === 0 && isImmediate === false) {
            lastExec = now;
        }
        var elapsed = now - lastExec;
        callbackArgs = args;
        ctx = this;
        clearExistingTimeout();
        if (elapsed > delay) {
            exec();
        }
        else {
            if (isDebounce) {
                clearExistingTimeout();
                lastExec = now;
            }
            timeoutID = setTimeout(exec, delay - elapsed);
        }
    }
    wrapper.cancel = cancel;
    return wrapper;
}
/**
 * 防抖函数
 *
 * ```js
 * const debounceRet = FastUtils.debounce(function(e){
 *   console.log('debounce');
 * });
 *
 * window.addEventListener('scroll', debounceRet);
 *
 * // debounceRet 将不可再执行
 * debounceRet.cancel();
 * ```
 *
 * @category Function
 * @param callback 调用的函数
 * @param delay 延迟时间，默认200ms
 * @param callbackOptions 内部参数
 * @return 返回的值，可以调用 `cancel` 取消执行
 */
function debounce(callback, delay, callbackOptions) {
    if (delay === void 0) { delay = 200; }
    var isImmediate = true;
    var isDebounce = true;
    if (typeof callbackOptions === 'object') {
        isImmediate = 'isImmediate' in callbackOptions ? !!callbackOptions.isImmediate : isImmediate;
        isDebounce = 'isDebounce' in callbackOptions ? !!callbackOptions.isDebounce : isDebounce;
    }
    return throttle(callback, delay, { isDebounce: isDebounce, isImmediate: isImmediate });
}
/**
 * 版本号比较
 *
 * ```js
 * FastUtils.compareVersion('1.0.0', '=', '1.0.0'); // => true
 * FastUtils.compareVersion('1.1.1.1', '>=', '1.0.0'); // => true
 * FastUtils.compareVersion('1.1.0', '<', '1.0.0'); // => false
 * ```
 *
 * @category Function
 * @param leftVersion
 * @param compareFlag 比较符号：'>', '>=', '=', '<=', '<'
 * @param rightVersion
 */
function compareVersion(leftVersion, compareFlag, rightVersion) {
    var leftVersionArr = leftVersion.split('.').map(function (i) { return parseInt(i, 10); });
    var rightVersionArr = rightVersion.split('.').map(function (i) { return parseInt(i, 10); });
    for (var i = 0; i < Math.max(leftVersionArr.length, rightVersionArr.length); i++) {
        var currentLeft = leftVersionArr[i];
        var currentRight = rightVersionArr[i];
        if (isNaN(currentLeft) || isNaN(currentRight)) {
            return false;
        }
        if ((currentLeft && !currentRight && currentLeft > 0) || currentLeft > currentRight) {
            return compareFlag === '>' || compareFlag === '>=';
        }
        else if ((currentRight && !currentLeft && currentRight > 0) || currentLeft < currentRight) {
            return compareFlag === '<' || compareFlag === '<=';
        }
    }
    return compareFlag === '=' || compareFlag === '<=' || compareFlag === '>=';
}
/**
 * 左填充字符串到指定长度
 *
 * ```js
 * FastUtils.padStart('1', 2, '0'); // => '01'
 * FastUtils.padStart('10', 2, '0'); // => '10'
 * ```
 *
 * @category Function
 * @param str 原字符串
 * @param length 指定长度
 * @param pad 填充的字符串
 */
function padStart(str, length, pad) {
    var s = String(str);
    if (!s || s.length >= length)
        return s;
    return "" + Array(length + 1 - s.length).join(pad) + str;
}
export { debounce, throttle, compareVersion, padStart };



//////////////////
// WEBPACK FOOTER
// ./node_modules/@ali/fast-utils/dist/function.js
// module id = null
// module chunks = 